import { ChangeDetectionStrategy, ChangeDetectorRef, Component } from '@angular/core';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import * as JSZip from 'jszip';
import groupBy from 'lodash-es/groupBy';
import isEqual from 'lodash-es/isEqual';
import isNil from 'lodash-es/isNil';
import { SubscriptionComponent } from '@liebherr/old-sage-ui';
import { BehaviorSubject, Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, map } from 'rxjs/operators';
import { AppState } from '../../../app.models';
import * as fromRouterTypes from '../../../router-types';
import { RouterTypeDto } from '../../../api/core';

@Component({
    selector: 'liebherr-add-firmwares-dialog',
    templateUrl: 'add-firmwares-dialog.component.html',
    styleUrls: ['add-firmwares-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddFirmwaresDialogComponent extends SubscriptionComponent {
    formGroup = this.fb.group({
        version: [undefined, Validators.required],
        file: [undefined, Validators.required],
        routerTypeId: [undefined, Validators.required],
    });

    unpacking$ = new BehaviorSubject<boolean>(false);
    routerTypes$: Observable<RouterTypeDto[]> = this.store.select(fromRouterTypes.selectRouterTypes);
    routerTypeOptions$: Observable<{ vendor: string; routerTypes: RouterTypeDto[] }[]> = this.routerTypes$.pipe(
        map((routerTypes) => this.getRouterTypeOptions(routerTypes))
    );
    zipError$ = new BehaviorSubject<string | undefined>(undefined);

    zipTypes = ['multipart/x-zip', 'application/zip', 'application/zip-compressed', 'application/x-zip-compressed'];

    errors$ = new BehaviorSubject<{ type: boolean; count: boolean }>({
        type: false,
        count: false,
    });

    constructor(
        private store: Store<AppState>,
        public dialogRef: MatDialogRef<AddFirmwaresDialogComponent>,
        private fb: UntypedFormBuilder,
        private cd: ChangeDetectorRef
    ) {
        super();

        this.sub.add(
            this.formGroup.valueChanges.pipe(distinctUntilChanged(isEqual), debounceTime(500)).subscribe((change) => {
                this.cd.detectChanges();
            })
        );
    }

    onCancel() {
        this.dialogRef.close();
    }

    onSubmit() {
        if (this.formGroup.valid && this.formGroup.dirty) {
            this.dialogRef.close(this.formGroup.getRawValue());
        }
    }

    onFilesChanged(files: FileList, event: any) {
        const count = files.length;
        if (count === 1) {
            const archiv = files[0];
            const isZip = this.zipTypes.includes(archiv?.type);

            if (isZip) {
                // clear errors
                this.errors$.next({ type: false, count: false });
                this.unpacking$.next(true);
                this.readZip(archiv);
            } else {
                // show type error
                this.errors$.next({ ...this.errors$.getValue(), type: true });
            }
        } else {
            // show count error
            this.errors$.next({ ...this.errors$.getValue(), count: true });
        }
    }

    private readZip(archiv: File) {
        // unpack zip file
        const zip = new JSZip();
        this.unpacking$.next(true);
        zip.loadAsync(archiv).then((zip) => {
            this.handleUnpackedFiles(zip);
        });
    }

    private handleUnpackedFiles(zip: any) {
        // extract data from unpacked files
        const promises: Promise<any>[] = [];
        const fileKeys = Object.keys(zip.files);
        const fwIdx = fileKeys.findIndex((k) => k.endsWith('.fw'));
        const versionIdx = fileKeys.findIndex((k) => k.endsWith('version.txt'));

        if (fwIdx > -1 && versionIdx > -1) {
            this.zipError$.next(undefined);
            promises.push(zip.files[fileKeys[fwIdx]].async('blob'));
            promises.push(zip.files[fileKeys[versionIdx]].async('string'));

            Promise.all(promises).then(([fw, version]) => {
                const filename = fwIdx > -1 ? fileKeys[fwIdx].slice(fileKeys[fwIdx].indexOf('/') + 1) : undefined;
                if (!isNil(filename)) {
                    this.updateForm(filename, fw, version);
                }

                this.unpacking$.next(false);
            });
        } else {
            if (fwIdx === -1 && versionIdx === -1) {
                this.zipError$.next('FIRMWARES.IMPORT.ERRORS.ZIP_FORMAT.BOTH_MISSING');
            } else if (fwIdx === -1 && versionIdx > -1) {
                this.zipError$.next('FIRMWARES.IMPORT.ERRORS.ZIP_FORMAT.FW_MISSING');
            } else if (fwIdx > -1 && versionIdx === -1) {
                this.zipError$.next('FIRMWARES.IMPORT.ERRORS.ZIP_FORMAT.VERSION_MISSING');
            }
            this.unpacking$.next(false);
        }
    }

    private updateForm(filename: string, fw?: Blob, version?: string) {
        if (fw) {
            const file = new File([fw], filename);
            this.formGroup.get('file')?.patchValue(file);
            this.formGroup.get('file')?.markAsDirty();
        }

        if (version) {
            this.formGroup.get('version')?.patchValue(version.trim());
            this.formGroup.get('version')?.markAsDirty();
        }
    }

    private getRouterTypeOptions(routerTypes: RouterTypeDto[]) {
        const vendors = groupBy(routerTypes, (t) => t.vendor || 'UNKNOWN');
        return Object.keys(vendors)
            .filter((k) => k !== 'UNKNOWN')
            .map((key) => ({
                vendor: key,
                routerTypes: vendors[key],
            }));
    }
}
